import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building05, Folder } from "@untitledui/icons";
import { useState } from "react";
import { useAdminMerchants } from "@/api/admin/merchants";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { usePagination } from "@/api/pagination";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { useDebounce } from "@/hooks/use-debounce";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { PageHeader } from "@/pages/admin/page-header";
import { AdminMerchantsTable } from "@/pages/admin/tables/merchants";
import { AdminWorkspacesTable } from "@/pages/admin/tables/workspaces";
import type { adminCustomersRoute } from "@/router";

const brandsTab = { id: "brands", label: "All Merchants", icon: <Building05 /> };
const workspacesTab = { id: "workspaces", label: "Workspaces", icon: <Folder /> };

export const CustomersPage = () => {
  const { tab, page } = useSearch({ strict: false }) as ReturnType<(typeof adminCustomersRoute)["useSearch"]>;
  const navigate = useNavigate() as ReturnType<(typeof adminCustomersRoute)["useNavigate"]>;
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const pagination = usePagination();

  const selectedTab = tab ?? "workspaces";

  // Both navigations push a history entry, so leaving for a detail page and coming
  // back restores the tab and page the user left from.
  const selectTab = (next: string) => navigate({ search: { tab: next as "workspaces" | "brands", page: 1 } });
  const selectPage = (next: number) => navigate({ search: (prev) => ({ ...prev, page: next }) });

  const tabs = [workspacesTab, brandsTab];

  // Sorted like the tables below so both share the cached page (counts only).
  const { data: merchantsData } = useAdminMerchants(
    pagination.pagination,
    { column: "name", direction: "asc" },
    { text: "" },
  );
  const { data: workspacesData } = useAdminWorkspaces(
    pagination.pagination,
    { column: "name", direction: "asc" },
    { text: "" },
  );

  const isBrandsTab = selectedTab === "brands";
  const count = isBrandsTab ? merchantsData?.total : workspacesData?.total;

  return (
    <div className="h-full flex flex-col gap-6 mt-4 px-12">
      <PageHeader
        title="Customers"
        subtitle={"All Merchants and Workspaces in our application"}
        count={count}
        search={search}
        onSearchChange={setSearch}
        actions={
          isBrandsTab ? (
            <Button onClick={() => navigate({ to: "/admin/merchants/new" })}>Add new merchant</Button>
          ) : (
            <Button onClick={() => navigate({ to: "/admin/workspaces/new" })}>Create new Workspace</Button>
          )
        }
        icon={Building05}
      />
      <Tabs selectedKey={selectedTab} onSelectionChange={(key) => selectTab(key as string)}>
        <Tabs.List type="underline" items={tabs}>
          {(item) => <Tabs.Item {...item} />}
        </Tabs.List>
      </Tabs>
      <div className="flex-1 min-h-0 flex flex-col overflow-auto">
        {isBrandsTab ? (
          <AdminMerchantsTable search={debouncedSearch} page={page} onPageChange={selectPage} />
        ) : (
          <AdminWorkspacesTable search={debouncedSearch} page={page} onPageChange={selectPage} />
        )}
      </div>
    </div>
  );
};
